import {
  ANNUAL,
  DIRECTDEBIT,
  MONTHLY,
  MONTHLY_2_MONTHS_FREE,
  MONTHLY_3_MONTHS_DEFERRED,
  PRODUCT,
  SUBMITTED,
} from "@/constants/common.constants.js";
import {
  INSURER_TYPE,
  UNKNOWN_INSURER,
  UNKNOWN_INSURER_NAME,
} from "@/constants/insurer.constants.js";
import moment from "moment";
import { applyCommissionSacrificeToQuote } from "./commission";
import { adStringToBool } from "./cover";
import { getProductGroup } from "./question-set";
import { sortInsurerMonthlyTotal } from "./sorting";
import { dereference, objectValuesToArray } from "./transforms";

export const findPremium = (premiums, paymentMethod, paymentFrequency) => {
  // Developers' choice to pass in either quote.Premiums or quote.OriginalPremiums
  if (!premiums) return null;
  return premiums.find(
    (prem) =>
      prem.PaymentMethod === paymentMethod &&
      prem.PaymentFrequency === paymentFrequency
  );
};

export const findPriceByPremium = (prices, premium) => {
  if (!premium) return null;
  return prices.find((price) => price.ID === premium.PriceID);
};

const insurerExitedPanel = (insurer, productGroup) => {
  if (!insurer) return true;

  const exitDate = getInsurerExitDate(insurer, productGroup);
  if (!exitDate) return false;

  const panelDatePast = moment(exitDate).isBefore(new Date());
  return panelDatePast;
};

const insurerOnProduct = (insurer, productGroup) => {
  if (!insurer) return false;
  // Forgive productGroup being nullish and assume the best
  if (!productGroup) return true;
  return insurer.productGroups.includes(productGroup);
};

export const getInsurerExitDate = (insurer, productGroup) => {
  if (!insurer || !insurer.onPanelUntil) {
    return null;
  }

  // string applies to all products
  if (typeof insurer.onPanelUntil === "string") {
    return insurer.onPanelUntil;
  }

  // object with product group keys
  if (typeof insurer.onPanelUntil === "object" && productGroup) {
    return insurer.onPanelUntil[productGroup] || null;
  }

  return null;
};

export const prepareQuotes = (
  quoteResponse,
  coverSelection,
  commissionSacrificePercent
) => {
  const quoteResponseCopy = dereference(quoteResponse);
  const isSubmitted = quoteResponseCopy.QuoteStatus === SUBMITTED;
  const quotes = isSubmitted
    ? quoteResponseCopy.Quotes.filter((q) => q.ApplicationId > 0)
    : quoteResponseCopy.Quotes;

  const hasPrices = quotes && quoteResponseCopy.Prices;
  if (!hasPrices) return [];

  const qsId = quoteResponseCopy.QuestionSetId;
  const productGroup = getProductGroup(qsId);

  // Named buckets, where each bucket has some top-level insurer info and an array of quotes for that insurer
  // e.g. { "axa" : { "isCheapest" : true, "quotes" : [ {q1}, {q2} ]} , "rsa" : ... };
  const insurerQuotesModel = {};

  quotes.forEach((quote) => {
    quote.BuildingsAD = adStringToBool(quote.Cover.BuildingsAD);
    quote.ContentsAD = adStringToBool(quote.Cover.ContentsAD);
    // 0 = No AD, 1 = Contents, 2 = Buildings, 3 = All
    quote.ADMask = 2 * quote.BuildingsAD + quote.ContentsAD;

    // Get the sacrificed Premiums and related Prices for this quote
    // reduced := { Premiums[], Prices[] }
    const reduced = applyCommissionSacrificeToQuote(
      quoteResponseCopy,
      quote,
      commissionSacrificePercent
    );

    // For PremiumBreakdowns to work, overwrite the Premiums and Prices on the quote:
    // Replace the quote.Premiums with the sacrificed set
    quote.Premiums = [...reduced.Premiums];

    // Set the quote.Prices to the sacrificed set
    // At this point the quote loses any Prices which reflect the OriginalPremiums
    quote.Prices = [...reduced.Prices];

    // Find PriceIDs for the relevant premiums
    const annualPremium = findPremium(quote.Premiums, DIRECTDEBIT, ANNUAL);
    const monthlyPremium = findPremium(quote.Premiums, DIRECTDEBIT, MONTHLY);
    const monthly3MonthsDeferredPremium = findPremium(
      quote.Premiums,
      DIRECTDEBIT,
      MONTHLY_3_MONTHS_DEFERRED
    );
    const monthly2MonthsFreePremium = findPremium(
      quote.Premiums,
      DIRECTDEBIT,
      MONTHLY_2_MONTHS_FREE
    );

    // Get the prices
    const annualPrice = findPriceByPremium(quote.Prices, annualPremium);
    const monthlyPrice = findPriceByPremium(quote.Prices, monthlyPremium);
    const monthlyDefferedPrice = findPriceByPremium(
      quote.Prices,
      monthly3MonthsDeferredPremium
    );
    const monthly2FreeMonthsDeferredPrice = findPriceByPremium(
      quote.Prices,
      monthly2MonthsFreePremium
    );

    // Stamp convenient totals on the quote object
    // TODO: Refactor these later into a Totals[] if time and scope/complexity allows
    if (annualPrice)
      quote.AnnualTotal = annualPrice.Values.TotalCostPerPaymentPeriod;

    if (monthlyPrice)
      quote.MonthlyTotal = monthlyPrice.Values.TotalCostPerPaymentPeriod;

    if (monthlyDefferedPrice)
      quote.Monthly3MonthsDeferredTotal =
        monthlyDefferedPrice.Values.TotalCostPerPaymentPeriod;

    if (monthly2FreeMonthsDeferredPrice)
      quote.Monthly2FreeMonthsDeferredTotal =
        monthly2FreeMonthsDeferredPrice.Values.TotalCostPerPaymentPeriod;

    // Get the insurer { name, image, onPanelUntil, productGroups }
    const insurer =
      INSURER_TYPE.find(
        (i) => i.name.toLowerCase() === quote.InsurerName.toLowerCase()
      ) ?? UNKNOWN_INSURER;

    // If there is no insurerModel in the bucket yet for this insurer, create it
    if (insurerQuotesModel[insurer.name] === undefined) {
      insurerQuotesModel[insurer.name] = {
        ...insurer,
        isCheapest: false,
        isAvailable: true,
        buildingsAd: false,
        contentsAd: false,
        selectedQuote: null,
        quotes: [],
      };

      if (insurer.name === UNKNOWN_INSURER_NAME)
        insurerQuotesModel[insurer.name].isAvailable = false;
    }

    // Push this quote into the insurer bucket
    insurerQuotesModel[insurer.name].quotes.push(quote);
  });

  if (quoteResponse.ProductId === PRODUCT.MPPI.toString()) {
    return prepareMPPIQuotes(insurerQuotesModel, productGroup);
  }

  // Set AD upgrade prices and cheapest quote/insurer
  INSURER_TYPE.forEach((insurer) => {
    const insurerModel = insurerQuotesModel[insurer.name];

    // No quotes for this insurer?
    if (!insurerModel) {
      // Insurer off panel:
      // Remove from results unless this is the submitted screen!
      if (insurerExitedPanel(insurer, productGroup) && !isSubmitted) {
        delete insurerQuotesModel[insurer.name];
        return;
      }

      // Insurer not on product (no grey row)
      if (!insurerOnProduct(insurer, productGroup)) return;

      // Show as not available (grey row)
      insurerQuotesModel[insurer.name] = {
        ...insurer,
        isAvailable: false,
      };
      return;
    }

    const quoteGroup = insurerModel.quotes;
    if (!quoteGroup) return;

    const AdNonQuote = quoteGroup.find((q) => q.ADMask === 0);
    const AdCQuote = quoteGroup.find((q) => q.ADMask === 1);
    const AdBQuote = quoteGroup.find((q) => q.ADMask === 2);
    const AdAllQuote = quoteGroup.find((q) => q.ADMask === 3);

    if (AdNonQuote) {
      AdNonQuote.BuildingsADPrice = null;
      AdNonQuote.ContentsADPrice = null;
    }

    if (AdAllQuote) {
      AdAllQuote.BuildingsADPrice = 0;
      AdAllQuote.ContentsADPrice = 0;
    }

    if (AdBQuote) {
      AdBQuote.BuildingsADPrice = 0;
      if (AdNonQuote) {
        AdNonQuote.BuildingsADPrice =
          AdBQuote.MonthlyTotal - AdNonQuote.MonthlyTotal;
      }

      if (AdAllQuote) {
        AdBQuote.ContentsADPrice =
          AdAllQuote.MonthlyTotal - AdBQuote.MonthlyTotal;
      } else {
        AdBQuote.BuildingsADPrice = null;
      }
    }

    if (AdCQuote) {
      AdCQuote.ContentsADPrice = 0;
      if (AdNonQuote) {
        AdNonQuote.ContentsADPrice =
          AdCQuote.MonthlyTotal - AdNonQuote.MonthlyTotal;
      }

      if (AdAllQuote) {
        AdCQuote.BuildingsADPrice =
          AdAllQuote.MonthlyTotal - AdCQuote.MonthlyTotal;
      } else {
        AdCQuote.BuildingsADPrice = null;
      }
    }

    const matchingQuote = getMatchingQuote(quoteGroup, coverSelection);

    insurerModel.selectedQuote = matchingQuote ?? AdNonQuote;
    insurerModel.buildingsAd = coverSelection.buildingsAd;
    insurerModel.contentsAd = coverSelection.contentsAd;
  });

  console.log("insurerQuotesModel", insurerQuotesModel);

  // For convenience of building up, insurerQuotesModel is a dictionary-like store
  // which is not iterable in JS. Now we turn it into a plain array.
  const iterableModels = objectValuesToArray(insurerQuotesModel);

  // Tag the cheapest insurer
  iterableModels.sort(sortInsurerMonthlyTotal);
  iterableModels[0].isCheapest = true;

  console.log("iterableModels", iterableModels);
  return iterableModels;
};

const prepareMPPIQuotes = (insurerQuotesModel, productGroup) => {
  const insurers = [];

  INSURER_TYPE.forEach((insurer) => {
    const insurerModel = insurerQuotesModel[insurer.name];
    if (!insurerModel) {
      // No quotes for this insurer...
      // Insurer off panel (no grey row)
      if (insurerExitedPanel(insurer)) return;

      // Insurer not on product (no grey row)
      if (!insurerOnProduct(insurer, productGroup)) return;

      // Show as not available (greyed out)
      insurerQuotesModel[insurer.name] = {
        ...insurer,
        isAvailable: false,
      };
      return;
    }

    const quoteGroup = insurerModel.quotes;
    if (!quoteGroup) return;

    quoteGroup.forEach((q) => {
      const insurer = {
        ...insurerModel,
        selectedQuote: q,
        basePrice: q.MonthlyTotal,
      };

      insurers.push(insurer);
    });
  });

  insurers.sort(sortInsurerMonthlyTotal);

  return insurers;
};

const getMatchingQuote = (quoteGroup, coverSelection) => {
  // Exact match - used most of the time
  const matchingQuote = quoteGroup.find(
    (q) =>
      q.BuildingsAD === coverSelection.buildingsAd &&
      q.ContentsAD === coverSelection.contentsAd
  );

  if (matchingQuote) return matchingQuote;

  // Fuzzy match - if we've upgraded from BO or CO to B&C
  // Allow a truthy bool match like null == false for one or the other AD type
  const fuzzyMatch = quoteGroup.find(
    (q) =>
      !!q.BuildingsAD == !!coverSelection.buildingsAd &&
      !!q.ContentsAD == !!coverSelection.contentsAd
  );
  return fuzzyMatch;
};
